import { useParams, NavLink } from 'react-router-dom';
import { useState, useMemo } from 'react';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import MangaCard from '@/components/base/MangaCard';
import { mangaCategories } from '@/mocks/categories';
import { mangaList } from '@/mocks/manga';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  const currentCategory = mangaCategories.find((c) => c.slug === slug);
  const categoryName = currentCategory?.name || '分类';

  const filteredManga = useMemo(() => {
    let list = mangaList.filter((m) => m.categorySlug === slug);
    if (sortBy === 'popular') {
      list = [...list].sort((a, b) => b.views - a.views);
    } else if (sortBy === 'rating') {
      list = [...list].sort((a, b) => b.rating - a.rating);
    }
    return list;
  }, [slug, sortBy]);

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryName} - 樱花动漫`,
    description: `樱花动漫${categoryName}专题，收录精选日本${categoryName}作品，在线免费阅读。`,
    url: `https://sakuracomic.cn/category/${slug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: filteredManga.map((manga, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'CreativeWork',
          name: manga.title,
          url: `https://sakuracomic.cn/article/${manga.id}`,
        },
      })),
    },
  };

  const sortOptions = [
    { value: 'latest' as const, label: '最新更新' },
    { value: 'popular' as const, label: '最受欢迎' },
    { value: 'rating' as const, label: '评分最高' },
  ];

  return (
    <>
      <SEOHead
        title={`${categoryName}漫画_免费${categoryName}_在线${categoryName}_樱花动漫`}
        description={`樱花动漫${categoryName}专题，收录精选日本${categoryName}作品，支持在线免费阅读。${currentCategory?.description || ''}`}
        keywords={`樱花动漫,${categoryName},在线漫画,日本漫画,免费漫画`}
        canonicalUrl={`https://sakuracomic.cn/category/${slug}`}
        schema={categorySchema}
      />

      <div className="bg-background-100 border-b border-background-200/70">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '分类' },
              { label: categoryName },
            ]}
          />
          <div className="mt-4">
            <h1 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950">
              {categoryName}
            </h1>
            {currentCategory && (
              <p className="text-sm text-foreground-600 mt-2">{currentCategory.description}</p>
            )}
          </div>
        </div>
      </div>

      <div className="bg-background-50 border-b border-background-200/70">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
          <div className="flex flex-wrap gap-2">
            <NavLink
              to="/category/shonen"
              className="text-xs px-3 py-1.5 rounded-full bg-background-100 text-foreground-600 hover:bg-accent-100/80 hover:text-accent-700 transition-colors whitespace-nowrap"
            >
              全部分类
            </NavLink>
            {mangaCategories.map((cat) => (
              <NavLink
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`text-xs px-3 py-1.5 rounded-full transition-colors whitespace-nowrap ${
                  cat.slug === slug
                    ? 'bg-accent-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-accent-100/80 hover:text-accent-700'
                }`}
              >
                {cat.name}
              </NavLink>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <div className="flex items-center gap-3 mb-6">
          <span className="text-sm text-foreground-600 whitespace-nowrap">排序：</span>
          <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
            {sortOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setSortBy(opt.value)}
                className={`text-xs px-4 py-1.5 rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                  sortBy === opt.value
                    ? 'bg-background-50 text-foreground-950 font-medium shadow-sm'
                    : 'text-foreground-600 hover:text-foreground-950'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <span className="text-xs text-foreground-600 ml-auto whitespace-nowrap">
            共 {filteredManga.length} 部作品
          </span>
        </div>

        {filteredManga.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
            {filteredManga.map((manga) => (
              <MangaCard key={manga.id} manga={manga} />
            ))}
          </div>
        ) : (
          <div className="py-20 text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-600">
              <i className="ri-emotion-sad-line text-2xl"></i>
            </div>
            <p className="mt-4 text-foreground-600">该分类暂无漫画作品</p>
            <NavLink
              to="/category/shonen"
              className="inline-block mt-4 px-6 py-2 rounded-full bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap"
            >
              浏览其他分类
            </NavLink>
          </div>
        )}
      </div>
    </>
  );
}